import React from 'react';
import {
  Box,
  Paper,
  Typography,
  Alert,
  Button,
  CircularProgress,
} from '@mui/material';
import DownloadIcon from '@mui/icons-material/Download';
import TranslateIcon from '@mui/icons-material/Translate';
import DashboardIcon from '@mui/icons-material/Dashboard';
import { useNavigate } from 'react-router-dom';
import { authFetch } from '../../utils/authHelpers';
import { BACKEND_URL } from '../../utils/apiConfig';

function handleDownload(content, filename) {
  const blob = new Blob([content], { type: 'text/plain;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

/**
 * Step 4 – Resultaat.
 */
function StepResult({
  emailSent,
  email,
  correctedSRT,
  editableSRT,
  fcpxml,
  karaokeASS,
  generatedSRT,
  detectedLanguage,
  ukEnglishSRT,
  convertingToUK,
  convertToUKEnglish,
  translatedSRT,
  translating,
  translateSubtitles,
  uploadToFrameIo,
  frameioLink,
  hasBurnedVideo,
  jobId,
  handleReset,
  emailAttemptedRef,
  setActiveStep,
  setIsRendering,
  aspectRatio,
  activePreset,
  overridePreset,
  defaultPreset,
  getBaseFilename,
}) {
  const navigate = useNavigate();

  return (
    <Box sx={{ mt: 3 }}>
      <Alert severity="success" sx={{ mb: 3 }}>
        <Typography variant="h6">Ondertiteling voltooid!</Typography>
        {emailSent && (
          <Typography variant="body2">📧 Email verstuurd naar {email}</Typography>
        )}
      </Alert>

      {/* Downloads Section */}
      <Paper variant="outlined" sx={{ p: 2, mb: 3 }}>
        <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 2 }}>
          📥 Downloads
        </Typography>
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
          {(correctedSRT || editableSRT) && (
            <Button
              variant="contained" size="small" startIcon={<DownloadIcon />}
              onClick={() => handleDownload(correctedSRT || editableSRT, `${getBaseFilename()}.srt`)}
            >
              SRT
            </Button>
          )}
          {fcpxml && (
            <Button
              variant="contained" color="secondary" size="small" startIcon={<DownloadIcon />}
              onClick={() => handleDownload(fcpxml, `${getBaseFilename()}.fcpxml`)}
            >
              FCP-XML
            </Button>
          )}
          {karaokeASS && (
            <Button
              variant="contained" color="warning" size="small" startIcon={<DownloadIcon />}
              onClick={() => handleDownload(karaokeASS, `${getBaseFilename()}_karaoke.ass`)}
            >
              ASS (Social Subs)
            </Button>
          )}
          {generatedSRT && (
            <Button
              variant="outlined" size="small" startIcon={<DownloadIcon />}
              onClick={() => handleDownload(generatedSRT, `${getBaseFilename()}_origineel.srt`)}
            >
              Origineel
            </Button>
          )}
        </Box>
      </Paper>

      {/* UK English Variant - only show when DETECTED language is English */}
      {(detectedLanguage === 'en' || detectedLanguage === 'english') && (correctedSRT || editableSRT) && (
        <Paper variant="outlined" sx={{ p: 2, mb: 3 }}>
          <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
            🇬🇧 UK English Variant
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Converteer Amerikaanse naar Brits-Engelse spelling (colour, realise, etc.). Timing blijft identiek.
          </Typography>
          <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
            {ukEnglishSRT ? (
              <>
                <Button
                  variant="contained" size="small" startIcon={<DownloadIcon />}
                  onClick={() => handleDownload(ukEnglishSRT, `${getBaseFilename()}_UK.srt`)}
                >
                  Download UK SRT
                </Button>
                {fcpxml && (
                  <Button
                    variant="outlined" size="small" startIcon={<DownloadIcon />}
                    onClick={() => handleDownload(ukEnglishSRT.replace(/\.srt$/i, '.fcpxml'), `${getBaseFilename()}_UK.fcpxml`)}
                  >
                    Download UK FCPXML
                  </Button>
                )}
              </>
            ) : (
              <Button
                variant="outlined" size="small"
                onClick={convertToUKEnglish}
                disabled={convertingToUK}
                startIcon={convertingToUK ? <CircularProgress size={16} /> : null}
              >
                {convertingToUK ? 'Converteren...' : '🇬🇧 Genereer UK versie'}
              </Button>
            )}
          </Box>
        </Paper>
      )}

      {/* Translation - auto-detect and offer opposite language */}
      {(correctedSRT || editableSRT) && detectedLanguage && (
        <Paper variant="outlined" sx={{ p: 2, mb: 3 }}>
          <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
            {(detectedLanguage === 'nl' || detectedLanguage === 'dutch')
              ? '🇬🇧 Engelse Vertaling'
              : '🇳🇱 Nederlandse Vertaling'}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {(detectedLanguage === 'nl' || detectedLanguage === 'dutch')
              ? 'Genereer een Engelse versie van de ondertitels. Timing blijft identiek.'
              : 'Genereer een Nederlandse versie van de ondertitels. Timing blijft identiek.'}
          </Typography>
          <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
            {translatedSRT ? (
              <>
                <Button
                  variant="contained" size="small" startIcon={<DownloadIcon />}
                  onClick={() => {
                    const suffix = (detectedLanguage === 'nl' || detectedLanguage === 'dutch') ? 'EN' : 'NL';
                    handleDownload(translatedSRT, `${getBaseFilename()}_${suffix}.srt`);
                  }}
                >
                  Download {(detectedLanguage === 'nl' || detectedLanguage === 'dutch') ? 'EN' : 'NL'} SRT
                </Button>
                {fcpxml && (
                  <Button
                    variant="outlined" size="small" startIcon={<DownloadIcon />}
                    onClick={async () => {
                      try {
                        const response = await authFetch(
                          `${BACKEND_URL}/Api/srt-generator/format`,
                          {
                            method: 'POST',
                            headers: { 'Content-Type': 'application/json' },
                            body: JSON.stringify({
                              srtContent: translatedSRT,
                              aspectRatio,
                              projectName: getBaseFilename(),
                              presetStyle: (() => {
                                const ep = (activePreset && !overridePreset) ? activePreset : defaultPreset;
                                return ep ? { font_name: ep.font_name, font_style: ep.font_style, font_size: ep.font_size, baseline: ep.baseline } : null;
                              })(),
                            }),
                          }
                        );
                        const data = await response.json();
                        const suffix = (detectedLanguage === 'nl' || detectedLanguage === 'dutch') ? 'EN' : 'NL';
                        handleDownload(data.fcpxml, `${getBaseFilename()}_${suffix}.fcpxml`);
                      } catch (err) {
                        console.error('FCP-XML generation for translation failed:', err);
                      }
                    }}
                  >
                    Download {(detectedLanguage === 'nl' || detectedLanguage === 'dutch') ? 'EN' : 'NL'} FCP-XML
                  </Button>
                )}
              </>
            ) : (
              <Button
                variant="outlined" size="small"
                onClick={translateSubtitles}
                disabled={translating}
                startIcon={translating ? <CircularProgress size={16} /> : null}
              >
                {translating
                  ? 'Vertalen...'
                  : (detectedLanguage === 'nl' || detectedLanguage === 'dutch')
                    ? '🇬🇧 Genereer Engelse versie'
                    : '🇳🇱 Genereer Nederlandse versie'}
              </Button>
            )}
          </Box>
        </Paper>
      )}

      {/* Batch Translation Link */}
      <Paper variant="outlined" sx={{ p: 2, mb: 3, backgroundColor: 'action.hover' }}>
        <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
          Meerdere bestanden vertalen?
        </Typography>
        <Button
          variant="outlined" size="small" startIcon={<TranslateIcon />}
          onClick={() => navigate('/BatchTranslate')}
        >
          Batch Vertaling
        </Button>
      </Paper>

      {/* Frame.io Status Section */}
      {uploadToFrameIo && (
        <Paper
          variant="outlined"
          sx={{
            p: 2,
            mb: 3,
            borderColor: frameioLink ? '#4caf50' : '#ff9800',
            backgroundColor: frameioLink ? '#f1f8e9' : '#fff8e1',
          }}
        >
          {frameioLink ? (
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <Box>
                <Typography variant="subtitle2" sx={{ color: '#2e7d32' }}>
                  ✅ Frame.io upload geslaagd
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Video met ingebakken ondertitels beschikbaar voor review
                </Typography>
              </Box>
              <Button
                variant="contained" color="success" size="small"
                href={frameioLink}
                target="_blank"
                rel="noopener noreferrer"
              >
                🎬 Bekijken in Frame.io →
              </Button>
            </Box>
          ) : (
            <Box>
              <Typography variant="subtitle2" sx={{ color: '#e65100' }}>
                ⚠️ Frame.io upload niet voltooid
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                De ondertitels zijn wel gegenereerd (SRT/FCP-XML), maar de video met ingebakken ondertitels
                is niet geüpload naar Frame.io. Dit kan gebeuren door een timeout of netwerkprobleem.
              </Typography>
              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center' }}>
                <Button
                  variant="outlined" size="small"
                  onClick={() => {
                    emailAttemptedRef.current = false;
                    setActiveStep(3);
                    setIsRendering(false);
                  }}
                >
                  🔄 Opnieuw proberen
                </Button>
                {(correctedSRT || editableSRT) && (
                  <Button
                    variant="contained" size="small" startIcon={<DownloadIcon />}
                    onClick={() => handleDownload(correctedSRT || editableSRT, `${getBaseFilename()}.srt`)}
                  >
                    Download SRT
                  </Button>
                )}
                {fcpxml && (
                  <Button
                    variant="contained" color="secondary" size="small" startIcon={<DownloadIcon />}
                    onClick={() => handleDownload(fcpxml, `${getBaseFilename()}.fcpxml`)}
                  >
                    Download FCP-XML
                  </Button>
                )}
                {hasBurnedVideo && jobId && (
                  <Button
                    variant="contained" size="small" startIcon={<DownloadIcon />}
                    sx={{ backgroundColor: '#d32f2f', '&:hover': { backgroundColor: '#b71c1c' } }}
                    onClick={() => {
                      const backendUrl = BACKEND_URL;
                      window.open(`${backendUrl}/Api/srt-generator/download-video/${jobId}`, '_blank');
                    }}
                  >
                    Download Video
                  </Button>
                )}
              </Box>
            </Box>
          )}
        </Paper>
      )}

      {/* Actions */}
      <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center' }}>
        <Button variant="outlined" onClick={handleReset}>
          Nieuwe SRT
        </Button>
        <Button
          variant="contained"
          startIcon={<DashboardIcon />}
          onClick={() => navigate('/SRTDashboard')}
        >
          Naar Dashboard
        </Button>
      </Box>
    </Box>
  );
}

export default StepResult;
